import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Input from 'components/Input';

import dotSvg from '/dot.svg';

import { cn } from 'utils/cn';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { scrollTop } from 'utils/scroll-top';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

import SynthesisTable from './components/SynthesisTable';

interface Props {
  isPDF?: boolean;
}

const Synthesis = ({ isPDF }: Props) => {
  const { startDate, setActiveTab, farm } = useCroptimizer();
  const { t } = useTranslation();
  const { setShowBack, setBackHandler, setShowNext } = useNextHandler();
  const date =
    startDate &&
    ('0' + startDate.getDate()).slice(-2) +
      '-' +
      ('0' + (startDate.getMonth() + 1)).slice(-2) +
      '-' +
      startDate.getFullYear();

  const goodReasons = [
    t('synthesis1'),
    t('synthesis2'),
    t('synthesis3'),
    t('synthesis4'),
    t('synthesis5'),
    t('synthesis6'),
  ];

  useEffect(() => {
    // Configure navigation handlers for the current step and enable Back buttons

    setShowNext(false);
    setShowBack(true);

    setBackHandler(() => {
      setActiveTab(4);
      scrollTop();
    });
  }, []);

  return (
    <div
      className={`border border-primitiveGrey rounded-2xl mb-6 flex flex-col p-8 ${
        !isPDF && 'md:p-4'
      }`}
    >
      <div
        className={cn('text-black font-bold text-xl md:text-lg', {
          'mb-8': !isPDF,
          'mb-2': isPDF,
        })}
      >
        {t('synthesis')}
      </div>
      <div
        className={cn('flex gap-2', {
          'mb-8': !isPDF,
          'mb-2': isPDF,
        })}
      >
        <div
          className={cn({
            'w-1/2': isPDF,
            'w-4/6 md:w-1/2 ': !isPDF,
          })}
        >
          <Input
            className="w-full"
            title={t('farmName')}
            titleClassName="bg-disable-gradient top-[-9px] left-[8px] truncate max-w-[85%]"
            value={farm}
            disabled
          />
        </div>
        <div
          className={cn({
            'w-1/2': isPDF,
            'w-2/6 md:w-1/2': !isPDF,
          })}
        >
          <Input
            className="w-full"
            title={t('date')}
            titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
            value={date ?? ''}
            disabled
          />
        </div>
      </div>
      <div
        className={cn('font-bold text-xl mb-8', {
          'mb-8': !isPDF,
          'mb-2': isPDF,
        })}
      >
        {t('omyaSolutions')}
      </div>
      <div>
        <SynthesisTable isPDF={isPDF} />
      </div>
      <div
        className={cn({
          'm-0': isPDF,
          'mt-4': isPDF,
        })}
      >
        <div
          className={cn('font-bold text-xl', {
            'my-2': isPDF,
            'my-4': !isPDF,
          })}
        >
          {t('goodReasons')}
        </div>
        {goodReasons.map((reasons: string, i: number) => (
          <div
            key={`${reasons}+ ${i}`}
            className={cn('flex items-center last:mb-0', {
              'mb-1': isPDF,
              'mb-2': !isPDF,
            })}
          >
            <img
              className="w-[6px] h-[6px] md:w-[6px] md:h-[6px] mr-2"
              src={dotSvg}
              alt=""
            />
            <span className="font-medium">{reasons}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Synthesis;
